import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Menu
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import MovieIcon from '@mui/icons-material/Movie';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';

const CATEGORIES = [
  { value: 'food', label: 'Food', color: '#FF5722', icon: RestaurantIcon },
  { value: 'transport', label: 'Transport', color: '#2196F3', icon: DirectionsCarIcon },
  { value: 'shopping', label: 'Shopping', color: '#E91E63', icon: ShoppingBagIcon },
  { value: 'entertainment', label: 'Entertainment', color: '#9C27B0', icon: MovieIcon },
  { value: 'utilities', label: 'Utilities', color: '#FFC107', icon: ReceiptIcon },
  { value: 'other', label: 'Other', color: '#757575', icon: MoreHorizIcon }
];

const ExpenseList = ({ list, onUpdateList }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedExpenseId, setSelectedExpenseId] = useState(null);

  // Form state
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('other');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  const items = list.items || [];

  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setDescription(expense.text || '');
      setAmount(expense.amount?.toString() || '');
      setCategory(expense.category || 'other');
      setDate(expense.date ? expense.date.split('T')[0] : new Date().toISOString().split('T')[0]);
    } else {
      setEditingExpense(null);
      setDescription('');
      setAmount('');
      setCategory('other');
      setDate(new Date().toISOString().split('T')[0]);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingExpense(null);
    setDescription('');
    setAmount('');
    setCategory('other');
    setDate(new Date().toISOString().split('T')[0]);
  };

  const handleSave = async () => {
    if (!description.trim() || !amount || parseFloat(amount) <= 0) return;

    const newExpense = {
      id: editingExpense ? editingExpense.id : Date.now().toString(),
      text: description.trim(),
      amount: parseFloat(amount),
      category,
      date: new Date(date).toISOString(),
      createdAt: editingExpense ? editingExpense.createdAt : new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };

    let updatedItems;
    if (editingExpense) {
      updatedItems = items.map(item => item.id === editingExpense.id ? newExpense : item);
    } else {
      updatedItems = [newExpense, ...items];
    }

    await onUpdateList(list.id, { items: updatedItems });
    handleCloseDialog();
  };

  const handleDelete = async (expenseId) => {
    const updatedItems = items.filter(item => item.id !== expenseId);
    await onUpdateList(list.id, { items: updatedItems });
    handleCloseMenu();
  };

  const handleMenuOpen = (event, expenseId) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedExpenseId(expenseId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedExpenseId(null);
  };

  const getCategoryInfo = (categoryValue) => {
    return CATEGORIES.find(c => c.value === categoryValue) || CATEGORIES[5];
  };

  const getTotalExpenses = () => {
    return items.reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  const getTotalByCategory = (categoryValue) => {
    return items
      .filter(item => item.category === categoryValue)
      .reduce((sum, item) => sum + (item.amount || 0), 0);
  };

  // Group expenses by date
  const groupedExpenses = items.reduce((groups, expense) => {
    const date = expense.date ? new Date(expense.date).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    }) : 'No Date';
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(expense);
    return groups;
  }, {});

  const renderExpenseRow = (expense) => {
    const catInfo = getCategoryInfo(expense.category);
    const IconComponent = catInfo.icon;

    return (
      <Box
        key={expense.id}
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 2,
          mb: 1,
          overflow: 'hidden',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            borderColor: 'rgba(255, 255, 255, 0.2)'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1.5,
            gap: 1.5
          }}
        >
          {/* Category Icon */}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: catInfo.color,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <IconComponent sx={{ color: 'white', fontSize: 20 }} />
          </Box>

          {/* Description and category */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body1"
              sx={{
                color: 'white',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {expense.text}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
            >
              {catInfo.label}
            </Typography>
          </Box>

          {/* Amount */}
          <Typography
            variant="body1"
            sx={{
              color: 'white',
              fontWeight: 600,
              fontSize: '1.1rem'
            }}
          >
            ${expense.amount?.toFixed(2)}
          </Typography>

          {/* Menu button */}
          <IconButton
            size="small"
            onClick={(e) => handleMenuOpen(e, expense.id)}
            sx={{ color: 'rgba(255, 255, 255, 0.5)' }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5" sx={{ color: 'white', fontWeight: 300 }}>
          {list.name}
        </Typography>
        <Button
          variant="contained"
          size="small"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: 'primary.main',
            '&:hover': { backgroundColor: 'primary.dark' }
          }}
        >
          Add
        </Button>
      </Box>

      {/* Total Card */}
      <Box
        sx={{
          backgroundColor: 'rgba(255, 255, 255, 0.08)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: 3,
          p: 2.5,
          mb: 3
        }}
      >
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', mb: 0.5 }}>
          Total Expenses
        </Typography>
        <Typography variant="h3" sx={{ color: 'white', fontWeight: 300 }}>
          ${getTotalExpenses().toFixed(2)}
        </Typography>

        {/* Category breakdown */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 2 }}>
          {CATEGORIES.filter(cat => getTotalByCategory(cat.value) > 0).map((cat) => (
            <Box key={cat.value} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: cat.color
                }}
              />
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                {cat.label}: ${getTotalByCategory(cat.value).toFixed(2)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Expenses grouped by date */}
      {Object.keys(groupedExpenses).length > 0 ? (
        Object.entries(groupedExpenses).map(([date, expenses]) => (
          <Box key={date} sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.5)',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                display: 'block',
                mb: 1
              }}
            >
              {date}
            </Typography>
            {expenses.map(renderExpenseRow)}
          </Box>
        ))
      ) : (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="body1" color="text.secondary">
            No expenses yet. Start tracking your spending!
          </Typography>
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(30, 30, 30, 0.95)',
            backdropFilter: 'blur(10px)'
          }
        }}
      >
        <DialogTitle>{editingExpense ? 'Edit Expense' : 'Add Expense'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
              autoFocus
            />

            <TextField
              fullWidth
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                label="Category"
              >
                {CATEGORIES.map((cat) => {
                  const IconComponent = cat.icon;
                  return (
                    <MenuItem key={cat.value} value={cat.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconComponent sx={{ color: cat.color, fontSize: 20 }} />
                        {cat.label}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!description.trim() || !amount || parseFloat(amount) <= 0}
          >
            {editingExpense ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={() => {
          const expense = items.find(item => item.id === selectedExpenseId);
          handleOpenDialog(expense);
          handleCloseMenu();
        }}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={() => handleDelete(selectedExpenseId)} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ExpenseList;
